import React, { useEffect, useMemo, useState } from "react";
import { Check, Copy, X } from "lucide-react";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export const createDefaultPromotionSchedule = () =>
  DAYS.map(() => ({
    enabled: true,
    lapses: [{ open: { hour: 0, minute: 0 }, close: { hour: 23, minute: 59 } }],
  }));

const formatTime = (time) =>
  `${String(time?.hour ?? 0).padStart(2, "0")}:${String(
    time?.minute ?? 0,
  ).padStart(2, "0")}`;

const parseTimeFromInput = (timeString) => {
  if (!timeString) {
    return { hour: 0, minute: 0 };
  }

  const [hoursRaw, minutesRaw] = String(timeString).split(":");
  const hour = Number(hoursRaw);
  const minute = Number(minutesRaw);

  return {
    hour: Number.isFinite(hour) ? hour : 0,
    minute: Number.isFinite(minute) ? minute : 0,
  };
};

const validateLapses = (lapses) => {
  const safeLapses = Array.isArray(lapses) ? lapses : [];

  const sortedLapses = [...safeLapses].sort((a, b) => {
    const aMinutes = (a?.open?.hour ?? 0) * 60 + (a?.open?.minute ?? 0);
    const bMinutes = (b?.open?.hour ?? 0) * 60 + (b?.open?.minute ?? 0);
    return aMinutes - bMinutes;
  });

  for (let index = 0; index < sortedLapses.length - 1; index += 1) {
    const current = sortedLapses[index];
    const next = sortedLapses[index + 1];

    const currentEndMinutes =
      (current?.close?.hour ?? 0) * 60 + (current?.close?.minute ?? 0);
    const nextStartMinutes =
      (next?.open?.hour ?? 0) * 60 + (next?.open?.minute ?? 0);

    if (currentEndMinutes >= nextStartMinutes) {
      return {
        isValid: false,
        message: `Lapse ${index + 1} ends at ${formatTime(
          current.close,
        )} but next lapse starts at ${formatTime(
          next.open,
        )}. There must be at least 1 minute gap.`,
      };
    }
  }

  return { isValid: true, message: "" };
};

const PromotionRulesSchedule = ({ schedule, onScheduleChange }) => {
  const [localSchedule, setLocalSchedule] = useState(() => {
    if (Array.isArray(schedule) && schedule.length > 0) {
      return schedule;
    }
    return createDefaultPromotionSchedule();
  });

  const [activeCopyDay, setActiveCopyDay] = useState(null);
  const [copyTargets, setCopyTargets] = useState([]);

  useEffect(() => {
    if (Array.isArray(schedule) && schedule.length > 0) {
      setLocalSchedule(schedule);
      return;
    }

    setLocalSchedule(createDefaultPromotionSchedule());
  }, [schedule]);

  const dayErrors = useMemo(() => {
    return DAYS.map((_, dayIndex) => {
      const daySchedule = localSchedule?.[dayIndex];
      if (!daySchedule || !daySchedule.enabled) {
        return "";
      }

      const validation = validateLapses(daySchedule.lapses);
      return validation.isValid ? "" : validation.message;
    });
  }, [localSchedule]);

  const commitSchedule = (nextSchedule) => {
    setLocalSchedule(nextSchedule);
    if (onScheduleChange) {
      onScheduleChange(nextSchedule);
    }
  };

  const handleToggleDayEnabled = (dayIndex) => {
    const nextSchedule = (localSchedule || []).map((day, index) => {
      if (index !== dayIndex) {
        return day;
      }

      return {
        ...day,
        enabled: !day?.enabled,
      };
    });

    commitSchedule(nextSchedule);
  };

  // const handleAddLapse = (dayIndex) => {
  //   const nextSchedule = (localSchedule || []).map((day, index) => {
  //     if (index !== dayIndex) {
  //       return day;
  //     }

  //     const previousLapses = Array.isArray(day?.lapses) ? day.lapses : [];

  //     return {
  //       ...day,
  //       lapses: [
  //         ...previousLapses,
  //         { open: { hour: 0, minute: 0 }, close: { hour: 23, minute: 59 } },
  //       ],
  //     };
  //   });

  //   commitSchedule(nextSchedule);
  // };

  // const handleRemoveLapse = (dayIndex, lapseIndex) => {
  //   const day = localSchedule?.[dayIndex];
  //   const lapses = Array.isArray(day?.lapses) ? day.lapses : [];

  //   if (lapses.length <= 1) {
  //     return;
  //   }

  //   const nextSchedule = (localSchedule || []).map((currentDay, index) => {
  //     if (index !== dayIndex) {
  //       return currentDay;
  //     }

  //     return {
  //       ...currentDay,
  //       lapses: lapses.filter((_, idx) => idx !== lapseIndex),
  //     };
  //   });

  //   commitSchedule(nextSchedule);
  // };

  const handleUpdateLapse = (dayIndex, lapseIndex, field, value) => {
    const nextSchedule = (localSchedule || []).map((day, index) => {
      if (index !== dayIndex) {
        return day;
      }

      const previousLapses = Array.isArray(day?.lapses) ? day.lapses : [];

      return {
        ...day,
        lapses: previousLapses.map((lapse, idx) => {
          if (idx !== lapseIndex) {
            return lapse;
          }

          return {
            ...lapse,
            [field]: { ...value },
          };
        }),
      };
    });

    commitSchedule(nextSchedule);
  };

  const handleTimeChange = (dayIndex, lapseIndex, field, timeString) => {
    const time = parseTimeFromInput(timeString);
    handleUpdateLapse(dayIndex, lapseIndex, field, time);
  };

  const handleToggleCopyTarget = (targetIndex) => {
    setCopyTargets((previous) =>
      previous.includes(targetIndex)
        ? previous.filter((item) => item !== targetIndex)
        : [...previous, targetIndex],
    );
  };

  const handleApplyCopy = (sourceDayIndex) => {
    const source = localSchedule?.[sourceDayIndex];
    if (!source) {
      return;
    }

    const clonedLapses = (source.lapses || []).map((lapse) => ({
      open: { ...lapse.open },
      close: { ...lapse.close },
    }));

    const nextSchedule = [...(localSchedule || [])];

    copyTargets.forEach((targetIndex) => {
      if (targetIndex === sourceDayIndex) {
        return;
      }

      const target = nextSchedule[targetIndex] || { enabled: true, lapses: [] };

      nextSchedule[targetIndex] = {
        ...target,
        enabled: source.enabled,
        lapses: clonedLapses,
      };
    });

    commitSchedule(nextSchedule);
    setActiveCopyDay(null);
    setCopyTargets([]);
  };

  const handleCloseCopyPanel = () => {
    setActiveCopyDay(null);
    setCopyTargets([]);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {DAYS.map((dayLabel, dayIndex) => {
        const daySchedule = localSchedule?.[dayIndex] || {
          enabled: true,
          lapses: [],
        };
        const lapses = Array.isArray(daySchedule.lapses)
          ? daySchedule.lapses
          : [];
        const validationError = dayErrors[dayIndex] || "";

        return (
          <div
            key={dayLabel}
            className="rounded-lg border border-gray-600 bg-gray-700 p-3 sm:p-4"
          >
            <div className="relative mb-3 flex items-center justify-between sm:mb-4">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <button
                  type="button"
                  onClick={() => handleToggleDayEnabled(dayIndex)}
                  className={`flex h-5 w-5 items-center justify-center rounded border-2 sm:h-6 sm:w-6 ${
                    daySchedule.enabled
                      ? "border-blue-500 bg-blue-500"
                      : "border-gray-400 bg-gray-600"
                  }`}
                  aria-label={`${
                    daySchedule.enabled ? "Disable" : "Enable"
                  } ${dayLabel}`}
                >
                  {daySchedule.enabled ? (
                    <Check className="h-3 w-3 text-white sm:h-4 sm:w-4" />
                  ) : null}
                </button>
                <span
                  className={`text-sm font-medium sm:text-base ${
                    daySchedule.enabled ? "text-white" : "text-gray-400"
                  }`}
                >
                  {dayLabel}
                </span>
              </div>

              <div className="flex items-center space-x-2">
                {/* {daySchedule.enabled && (
                  <button
                    type="button"
                    onClick={() => handleAddLapse(dayIndex)}
                    className="flex items-center space-x-1 text-blue-400 hover:text-blue-300 text-xs sm:text-sm"
                    aria-label={`Add time period for ${dayLabel}`}
                  >
                    <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Add Time Period</span>
                    <span className="sm:hidden">Add</span>
                  </button>
                )} */}

                <button
                  type="button"
                  onClick={() =>
                    setActiveCopyDay((previous) =>
                      previous === dayIndex ? null : dayIndex,
                    )
                  }
                  className="rounded border border-gray-500 p-1.5 text-gray-300 hover:text-white sm:p-2"
                  aria-label="Copy times to other days"
                  title="Copy times to other days"
                >
                  <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                </button>
              </div>
            </div>

            {activeCopyDay === dayIndex && (
              <div className="mt-3 rounded-lg border border-gray-600 bg-gray-700 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs text-gray-300 sm:text-sm">
                    Copy times to:
                  </p>
                  <button
                    type="button"
                    onClick={handleCloseCopyPanel}
                    className="rounded border border-gray-500 p-1.5 text-gray-300 hover:text-white"
                    aria-label="Close copy"
                  >
                    <X className="h-3 w-3 sm:h-4 sm:w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {DAYS.map((label, targetIndex) => {
                    if (targetIndex === dayIndex) {
                      return null;
                    }

                    const isChecked = copyTargets.includes(targetIndex);

                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => handleToggleCopyTarget(targetIndex)}
                        className={`rounded border px-2 py-1.5 text-left text-xs sm:text-sm ${
                          isChecked
                            ? "border-blue-500 bg-blue-500 text-white"
                            : "border-gray-500 bg-gray-600 text-gray-100"
                        }`}
                        aria-label={`Copy to ${label}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => handleApplyCopy(dayIndex)}
                    disabled={copyTargets.length === 0}
                    className="rounded bg-blue-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
                    aria-label="Apply copy"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}

            {daySchedule.enabled && (
              <div className="space-y-2 sm:space-y-3">
                {lapses.map((lapse, lapseIndex) => (
                  <div
                    key={`${dayLabel}-${lapseIndex}`}
                    className="flex flex-col gap-2 rounded-lg bg-gray-600 p-2 sm:flex-row sm:items-center sm:gap-4 sm:p-3"
                  >
                    <div className="flex-1">
                      <label className="mb-1 block text-xs text-gray-400">
                        Open
                      </label>
                      <input
                        type="time"
                        value={formatTime(lapse.open)}
                        onChange={(event) =>
                          handleTimeChange(
                            dayIndex,
                            lapseIndex,
                            "open",
                            event.target.value,
                          )
                        }
                        className="w-full rounded border border-gray-500 bg-gray-700 px-2 py-1.5 text-xs text-white focus:ring-2 focus:ring-blue-500 focus:outline-none sm:px-3 sm:py-2 sm:text-sm"
                        aria-label={`${dayLabel} open time for lapse ${
                          lapseIndex + 1
                        }`}
                      />
                    </div>

                    <div className="flex-1">
                      <label className="mb-1 block text-xs text-gray-400">
                        Close
                      </label>
                      <input
                        type="time"
                        value={formatTime(lapse.close)}
                        onChange={(event) =>
                          handleTimeChange(
                            dayIndex,
                            lapseIndex,
                            "close",
                            event.target.value,
                          )
                        }
                        className="w-full rounded border border-gray-500 bg-gray-700 px-2 py-1.5 text-xs text-white focus:ring-2 focus:ring-blue-500 focus:outline-none sm:px-3 sm:py-2 sm:text-sm"
                        aria-label={`${dayLabel} close time for lapse ${
                          lapseIndex + 1
                        }`}
                      />
                    </div>

                    {/* <div className="flex items-center pt-4">
                      <button
                        type="button"
                        onClick={() => handleRemoveLapse(dayIndex, lapseIndex)}
                        disabled={lapses.length <= 1}
                        className={`p-1.5 sm:p-2 transition-colors ${
                          lapses.length > 1
                            ? 'text-red-400 hover:text-red-300 cursor-pointer'
                            : 'text-gray-400 cursor-not-allowed opacity-50'
                        }`}
                        aria-label={`Remove time period from ${dayLabel}`}
                        title={
                          lapses.length > 1
                            ? 'Remove time period'
                            : 'At least one time period required'
                        }
                      >
                        <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div> */}
                  </div>
                ))}

                {validationError && (
                  <div className="rounded border border-red-500 bg-red-900/20 p-2 text-xs text-red-400 sm:text-sm">
                    {validationError}
                  </div>
                )}
              </div>
            )}

            {!daySchedule.enabled && (
              <p className="mt-3 text-xs text-gray-500 italic">
                Closed on {dayLabel}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PromotionRulesSchedule;
